/**
 * Date-of-birth bounds helper.
 *
 * The only `date`-typed inline-form field the backend emits is the
 * patient's Date of Birth (`booking.py::_WEB_FORM_FIELD_SPECS` /
 * `_VIEW_DETAILS_FORM_FIELD_SPECS`). A DOB can never be in the future,
 * so the widget caps both the native date-picker (`max` attribute) and
 * the submit-time validation at "today".
 *
 * This mirrors the backend's own guard
 * (`src/hana/engines/flows/booking.py::_parse_dob`, which rejects
 * `parsed > date.today()`). The widget cap is a UX convenience + a
 * client-side echo of that contract; the backend remains the
 * authoritative validator.
 *
 * `todayLocalISO()` formats the LOCAL calendar date as `YYYY-MM-DD`.
 * `Date.prototype.toISOString()` is deliberately NOT used: it converts
 * to UTC first, which rolls the date back a day for any local time
 * before 08:00 in Singapore (UTC+8) — a tester picking "today" just
 * before 8am would otherwise see yesterday as the max.
 */

/** The local calendar date as `YYYY-MM-DD` (NOT UTC — see module note). */
export function todayLocalISO(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * True when `value` (an ISO `YYYY-MM-DD` string) is a calendar date
 * strictly after today. Non-parseable / empty input returns `false`
 * (format validation is handled separately by the caller).
 */
export function isFutureDate(value: string): boolean {
  if (!value) return false;
  return value > todayLocalISO();
}
